import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { openModal } from 'reducers/modals';
// components
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
import { FormattedMessage } from 'react-intl';
import SurveyPreviewModal from './SurveyPreviewModal';
import ReactSelect from 'components/Select/ReactSelect';
import Button from 'components/Button';
import ButtonRadioGroup from 'components/ButtonRadioGroup/ButtonRadioGroup';
import AsyncMultiSelect from 'components/AsyncMultiSelect';
import Input from 'components/Input';
import MonthDaySelect from 'components/MonthDaySelect/MonthDaySelect';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
// utils
import isEqual from 'lodash.isequal';
import classnames from 'classnames/bind';
import moment from 'moment';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser, getMomentDisplayFormat } from 'utils/regionalDateHelper';
// endpoints
import { updateCustomSurvey, addCustomSurvey, removeCustomSurvey } from 'endpoints/tenantSettings';
// styles
import styles from './SurveyBox.module.scss';

const periodTypes = [
  { value: 'day', label: <FormattedMessage id="general.timeOptions.daily" /> },
  { value: 'week', label: <FormattedMessage id="general.timeOptions.weekly" /> },
  { value: 'month', label: <FormattedMessage id="general.timeOptions.monthly" /> },
  { value: 'year', label: <FormattedMessage id="general.timeOptions.yearly" /> },
];

const questionTypeOptions = [
  { value: false, label: <FormattedMessage id="general.yesOrNo" /> },
  { value: true, label: <FormattedMessage id="general.input" /> },
];

const farmAssignmentOptions = [
  { value: true, label: <FormattedMessage id="general.allFarms" /> },
  { value: false, label: <FormattedMessage id="general.selectedFarms" /> },
];

const endDateOptions = [
  { value: true, label: <FormattedMessage id="general.never" /> },
  { value: false, label: <FormattedMessage id="general.date" /> },
];

const seasonOptions = [
  { value: true, label: <FormattedMessage id="component.settings.customSurvey.yearRound" /> },
  { value: false, label: <FormattedMessage id="component.settings.customSurvey.dateRange" /> },
];

const reminderOptions = [
  { value: true, label: <FormattedMessage id="general.status.off" /> },
  { value: false, label: <FormattedMessage id="general.status.on" /> },
];

const SEASON_BOUND_PATTERN = /^\d{2}-\d{2}$/;
// Longer gaps are a yearly question, not a weekly one
const MAX_WEEKLY_INTERVAL = 52;
// A reminder later than a year can never land inside a cycle
const MAX_REMINDER_DAYS = 365;

const farmFilterOption = (values) => (option) => (
  values.some((farm) => farm.id === option.value) ? null : option
);

const newSurvey = {
  open_answer: false,
  all_farms: true,
  farms: [],
  end_date: null,
  frequency_interval: 1,
  season_start: null,
  season_end: null,
  reminder_after_no_days: null,
};

const cn = classnames.bind(styles);

class SurveyBox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      surveys: props.surveys.length ? props.surveys : [{ ...newSurvey }],
    };
    this.textareaRefs = {};
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);
    this.resizeTextareas();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.surveys, this.props.surveys)) {
      this.setState(({ surveys }) => ({ surveys: this.props.surveys.length || surveys.length ?
        [...this.props.surveys, ...surveys.filter((survey) => !survey.id)] :
        [{ ...newSurvey }] }));
    }
    this.resizeTextareas();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
  }

  // Grow textarea to fit its content, capped by CSS max-height
  resizeTextarea = (node) => {
    if (!node) return;
    const el = node;
    el.style.height = 'auto';
    const borderHeight = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + borderHeight}px`;
  };

  resizeTextareas = () => {
    Object.keys(this.textareaRefs).forEach((key) => this.resizeTextarea(this.textareaRefs[key]));
  };

  setTextareaRef = (i) => (node) => {
    if (node) {
      this.textareaRefs[i] = node;
      this.resizeTextarea(node);
    } else {
      delete this.textareaRefs[i];
    }
  };

  handleWindowResize = () => {
    if (this.resizeFrame) return;
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.resizeTextareas();
    });
  };

  addSurvey = () => { this.setState(({ surveys }) => ({ surveys: [...surveys, { ...newSurvey }] })); };

  removeSurvey = (i) => { this.setState(({ surveys }) => ({ surveys: surveys.filter((_, idx) => i !== idx) })); };

  cancelEdit = (i) => {
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? this.props.surveys[i] : survey))
    }));
  };

  onTitleChange = (i) => ({ target: { value } }) => {
    if (value.length <= 750) {
      this.setState(({ surveys }) => ({
        surveys: surveys.map((survey, idx) => (idx === i ? { ...survey, title: value } : survey))
      }));
    }
  };

  onDateChange = (i) => (value) => {
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ?
        { ...survey, start_date: formatDateForUser(value, this.props.countryCode) } :
        survey))
    }));
  };

  onFrequencyChange = (i) => ({ value }) => {
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? {
        ...survey,
        frequency: value,
        // Only weekly questions repeat every N weeks
        frequency_interval: value === 'week' ? (survey.frequency_interval || 1) : 1,
        // A daily question comes back tomorrow anyway, so a reminder can never fit in
        reminder_after_no_days: value === 'day' ? null : survey.reminder_after_no_days,
        start_date: formatDateForUser(moment(), this.props.countryCode)
      } :
        survey))
    }));
  };

  onIntervalChange = (i) => ({ target: { value, validity } }) => {
    if (!validity.valid) return;
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (
        idx === i ? { ...survey, frequency_interval: value ? +value : '' } : survey
      ))
    }));
  };

  onEndDateChange = (i) => (value) => {
    const formatted = formatDateForUser(value, this.props.countryCode);
    // Empty string keeps the date mode active with a blank picker; null is reserved for the "never" mode
    const endDate = typeof formatted === 'string' ? formatted : '';
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? { ...survey, end_date: endDate } : survey))
    }));
  };

  onEndDateModeChange = (i) => ({ target: { value } }) => {
    const never = value === 'true';
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? {
        ...survey,
        end_date: never ? null : (survey.start_date || formatDateForUser(moment(), this.props.countryCode)),
      } : survey))
    }));
  };

  onSeasonModeChange = (i) => ({ target: { value } }) => {
    const yearRound = value === 'true';
    // Coming back to the date range restores the saved season instead of blank pickers
    const saved = this.props.surveys[i] || {};
    // null is the year-round mode; empty string keeps the date range mode active with blank pickers
    const seasonStart = yearRound ? null : (saved.season_start || '');
    const seasonEnd = yearRound ? null : (saved.season_end || '');
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (
        idx === i ? { ...survey, season_start: seasonStart, season_end: seasonEnd } : survey
      ))
    }));
  };

  onSeasonBoundChange = (i, key) => (value) => {
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? { ...survey, [key]: value } : survey))
    }));
  };

  onReminderModeChange = (i) => ({ target: { value } }) => {
    const off = value === 'true';
    // Turning it back on restores the saved delay; empty string keeps the input shown but blank
    const saved = this.props.surveys[i] || {};
    const days = off ? null : (saved.reminder_after_no_days || '');
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? { ...survey, reminder_after_no_days: days } : survey))
    }));
  };

  onReminderDaysChange = (i) => ({ target: { value, validity } }) => {
    if (!validity.valid) return;
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (
        idx === i ? { ...survey, reminder_after_no_days: value ? +value : '' } : survey
      ))
    }));
  };

  isEndDateOutsideRange = (i) => (day) => {
    const { start_date } = this.state.surveys[i];
    const dateFormat = getMomentDisplayFormat(this.props.countryCode);
    const minDate = start_date && !start_date.includes('_') ? moment(start_date, dateFormat) : moment();
    return !day.isSameOrAfter(minDate, 'day');
  };

  isEndDateInvalid = ({ start_date, end_date }) => {
    if (end_date == null) return false;
    if (typeof end_date !== 'string' || !end_date || end_date.includes('_')) return true;
    const dateFormat = getMomentDisplayFormat(this.props.countryCode);
    const start = moment(start_date, dateFormat);
    const end = moment(end_date, dateFormat);
    return !end.isValid() || end.isBefore(start, 'day');
  };

  isIntervalInvalid = ({ frequency, frequency_interval }) => (
    frequency === 'week' &&
    !(frequency_interval >= 1 && frequency_interval <= MAX_WEEKLY_INTERVAL && frequency_interval % 1 === 0)
  );

  isSeasonInvalid = ({ season_start, season_end }) => {
    if (season_start == null && season_end == null) return false;
    return !SEASON_BOUND_PATTERN.test(season_start || '') || !SEASON_BOUND_PATTERN.test(season_end || '');
  };

  isReminderInvalid = ({ reminder_after_no_days }) => (
    reminder_after_no_days != null &&
    !(reminder_after_no_days >= 1 && reminder_after_no_days <= MAX_REMINDER_DAYS && reminder_after_no_days % 1 === 0)
  );

  onQuestionTypeChange = (i) => ({ target: { value } }) => {
    const openAnswer = value === 'true';
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ? {
        ...survey,
        open_answer: openAnswer,
        // Only a yes/no question can be answered "No"
        reminder_after_no_days: openAnswer ? null : survey.reminder_after_no_days,
      } : survey))
    }));
  };

  onFarmAssignmentChange = (i) => ({ target: { value } }) => {
    const allFarms = value === 'true';
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ?
        { ...survey, all_farms: allFarms, farms: allFarms ? [] : (survey.farms || []) } :
        survey))
    }));
  };

  onFarmSelect = (i) => (option) => {
    if (!option) return;
    const farm = { id: option.value, name: option.item ? option.item.name : option.label };
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => {
        if (idx !== i) return survey;
        const farms = survey.farms || [];
        if (farms.some((item) => item.id === farm.id)) return survey;
        return { ...survey, farms: [...farms, farm] };
      })
    }));
  };

  onFarmRemove = (i) => (farm) => {
    this.setState(({ surveys }) => ({
      surveys: surveys.map((survey, idx) => (idx === i ?
        { ...survey, farms: (survey.farms || []).filter((item) => item.id !== farm.id) } :
        survey))
    }));
  };

  openPreview = (i) => {
    const { openModal } = this.props;
    const { surveys } = this.state;
    openModal(<SurveyPreviewModal survey={surveys[i].title} openAnswer={!!surveys[i].open_answer} />);
  };

  formatSurveyFromResponse = (resource) => ({
    ...resource,
    start_date: formatDateForUser(resource.start_date, this.props.countryCode),
    end_date: resource.end_date ? formatDateForUser(resource.end_date, this.props.countryCode) : null,
  });

  onSave = (i) => {
    const { surveys } = this.state;
    const dateFormat = getMomentDisplayFormat(this.props.countryCode);
    const data = {
      ...surveys[i],
      start_date: moment(surveys[i].start_date, dateFormat).format('LLLL'),
      end_date: surveys[i].end_date ? moment(surveys[i].end_date, dateFormat).format('LLLL') : null,
      active: true,
      farm_ids: (surveys[i].farms || []).map((farm) => farm.id),
    };

    addCustomSurvey(data).then((resource) => {
      const { setSettings } = this.props;
      this.setState(({ surveys }) => ({
        surveys: surveys.filter((_, idx) => idx !== i)
      }));
      setSettings({ surveys: [...this.props.surveys, this.formatSurveyFromResponse(resource)] });
    }).catch(toastResponseErrors);
  };

  updateSurvey = (i) => {
    const { surveys } = this.state;
    const { setSettings } = this.props;
    const dateFormat = getMomentDisplayFormat(this.props.countryCode);
    const data = {
      ...surveys[i],
      start_date: moment(surveys[i].start_date, dateFormat).format('LLLL'),
      end_date: surveys[i].end_date ? moment(surveys[i].end_date, dateFormat).format('LLLL') : null,
      farm_ids: (surveys[i].farms || []).map((farm) => farm.id),
    };
    updateCustomSurvey(surveys[i].id, data).then((resource) => {
      const updatedSurvey = this.formatSurveyFromResponse(resource);
      setSettings({ surveys: this.props.surveys.map((survey, idx) => (idx === i ? updatedSurvey : survey)) });
    }).catch(toastResponseErrors);
  };

  onRemove = (i) => {
    const { surveys, setSettings } = this.props;
    removeCustomSurvey(surveys[i].id).then(() => {
      this.setState(({ surveys }) => ({ surveys: surveys.filter((_, idx) => idx !== i) }));
      setSettings({ surveys: surveys.filter((_, idx) => idx !== i) });
    }).catch(toastResponseErrors);
  };

  showDeleteConfirmation = (i) => {
    const { openModal } = this.props;
    openModal(<ConfirmationModal
      noBorder
      warningMessage={(
        <FormattedMessage
          id="component.settings.customSurvey.confirmModal"
          values={{ red: (value) => <span style={{ color: 'red' }}>{value}</span> }}
        />
      )}
      handleConfirm={() => this.onRemove(i)}
      actionBtnLabel={<FormattedMessage id="component.settings.customSurvey.deleteSurvey" />}
      actionBtnProps={{ red: true }}
      title={<FormattedMessage id="component.settings.customSurvey.deleteSurvey" />}
    />);
  };

  hasSurveyChanged = (i) => {
    const { surveys } = this.props;
    return !isEqual(surveys[i], this.state.surveys[i]);
  };

  render() {
    const { surveys } = this.state;
    const { countryCode } = this.props;
    const dateFormat = getMomentDisplayFormat(countryCode);
    return (
      <div className={cn('survey-box')}>
        {surveys.map((
          {
            frequency, frequency_interval, title, start_date, end_date, season_start, season_end, id, open_answer,
            all_farms, farms, reminder_after_no_days,
          },
          i
        ) => (
          <div className={cn('survey-item')} key={i}>
            <div key={i} className={cn('survey-body', 'mb-15')}>
              <div className={cn('survey-item-box')}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage
                    id="component.settings.customSurvey.questionLength"
                    values={{
                      max: 750,
                      current: title ? title.length : 0,
                    }}
                  />
                </div>
                <div className={cn('question-textarea-wrapper', { 'saved': !this.hasSurveyChanged(i) && id })}>
                  <FormattedMessage id="general.placeholder.typeQuestion">
                    {(placeholder) => (
                      <textarea
                        ref={this.setTextareaRef(i)}
                        className={cn('question-textarea')}
                        onChange={this.onTitleChange(i)}
                        value={title || ''}
                        placeholder={placeholder}
                        rows={4}
                      />
                    )}
                  </FormattedMessage>
                </div>
              </div>
              <div className={cn('survey-item-box', 'question-type')}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage id="component.settings.customSurvey.questionType" />
                </div>
                <ButtonRadioGroup
                  name={`question-type-${i}`}
                  options={questionTypeOptions}
                  value={!!open_answer}
                  onChange={this.onQuestionTypeChange(i)}
                  disabled={!!id}
                />
              </div>
              <div className={cn('survey-item-box', 'frequency')}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage id="general.frequency" />
                </div>
                <ReactSelect
                  noInputMargin
                  options={periodTypes}
                  placeholder={<FormattedMessage id="general.choose" />}
                  value={frequency}
                  onChange={this.onFrequencyChange(i)}
                  labelKey="label"
                  matchProp="value"
                  clearable={false}
                  searchable={false}
                />
              </div>
              {frequency === 'week' && (
                <div className={cn('survey-item-box', 'repeat-every')}>
                  <div className={cn('survey-item-label')}>
                    <FormattedMessage id="component.settings.customSurvey.repeatEvery" />
                  </div>
                  <div className={cn('interval-row')}>
                    <Input
                      className={cn('interval-input')}
                      hasNoMargin
                      type="number"
                      min={1}
                      max={MAX_WEEKLY_INTERVAL}
                      step={1}
                      value={frequency_interval ?? ''}
                      onChange={this.onIntervalChange(i)}
                      hasError={this.isIntervalInvalid({ frequency, frequency_interval })}
                    />
                    <span className={cn('interval-unit')}>
                      <FormattedMessage
                        id="component.settings.customSurvey.weeksCount"
                        values={{ count: frequency_interval || 0 }}
                      />
                    </span>
                  </div>
                </div>
              )}
              <div className={cn('survey-item-box', 'start-date', { 'hidden': !frequency })}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage id="general.startDate" />
                </div>
                <SingleDatePicker
                  className={cn('survey-date')}
                  date={start_date ? moment(start_date, dateFormat) : null}
                  onDateChange={this.onDateChange(i)}
                  isOutsideRange={(day) => !(day.isSameOrAfter(moment(), 'day'))}
                  displayFormat={dateFormat}
                  placeholder={dateFormat}
                />
              </div>
              <div className={cn('survey-item-box', 'end-date', { 'hidden': !frequency })}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage id="general.endDate" />
                </div>
                <ButtonRadioGroup
                  name={`end-date-mode-${i}`}
                  options={endDateOptions}
                  value={end_date == null}
                  onChange={this.onEndDateModeChange(i)}
                />
                {end_date != null && (
                  <SingleDatePicker
                    className={cn('survey-date', 'end-date-picker')}
                    date={end_date && !end_date.includes('_') ? moment(end_date, dateFormat) : null}
                    onDateChange={this.onEndDateChange(i)}
                    isOutsideRange={this.isEndDateOutsideRange(i)}
                    displayFormat={dateFormat}
                    placeholder={dateFormat}
                  />
                )}
              </div>
            </div>
            <div className={cn('season')}>
              <div className={cn('survey-item-label')}>
                <FormattedMessage id="component.settings.customSurvey.activeSeason" />
              </div>
              <ButtonRadioGroup
                name={`season-mode-${i}`}
                className={cn('season-radio')}
                options={seasonOptions}
                value={season_start == null}
                onChange={this.onSeasonModeChange(i)}
              />
              {season_start != null && (
                <>
                  <div className={cn('season-range')}>
                    <div className={cn('season-bound')}>
                      <div className={cn('survey-item-label')}>
                        <FormattedMessage id="component.settings.customSurvey.seasonFrom" />
                      </div>
                      <MonthDaySelect value={season_start} onChange={this.onSeasonBoundChange(i, 'season_start')} />
                    </div>
                    <div className={cn('season-bound')}>
                      <div className={cn('survey-item-label')}>
                        <FormattedMessage id="component.settings.customSurvey.seasonTo" />
                      </div>
                      <MonthDaySelect value={season_end} onChange={this.onSeasonBoundChange(i, 'season_end')} />
                    </div>
                  </div>
                  <div className={cn('season-hint')}>
                    <FormattedMessage id="component.settings.customSurvey.seasonHint" />
                  </div>
                </>
              )}
            </div>
            {!open_answer && frequency && frequency !== 'day' && (
              <div className={cn('reminder')}>
                <div className={cn('survey-item-label')}>
                  <FormattedMessage id="component.settings.customSurvey.reminderAfterNo" />
                </div>
                <ButtonRadioGroup
                  name={`reminder-mode-${i}`}
                  className={cn('reminder-radio')}
                  options={reminderOptions}
                  value={reminder_after_no_days == null}
                  onChange={this.onReminderModeChange(i)}
                />
                {reminder_after_no_days != null && (
                  <>
                    <div className={cn('reminder-row')}>
                      <Input
                        className={cn('reminder-input')}
                        hasNoMargin
                        type="number"
                        min={1}
                        max={MAX_REMINDER_DAYS}
                        step={1}
                        value={reminder_after_no_days ?? ''}
                        onChange={this.onReminderDaysChange(i)}
                        hasError={this.isReminderInvalid({ reminder_after_no_days })}
                      />
                      <span className={cn('reminder-unit')}>
                        <FormattedMessage
                          id="component.settings.customSurvey.daysCount"
                          values={{ count: reminder_after_no_days || 0 }}
                        />
                      </span>
                    </div>
                    <div className={cn('reminder-hint')}>
                      <FormattedMessage id="component.settings.customSurvey.reminderHint" />
                    </div>
                  </>
                )}
              </div>
            )}
            <div className={cn('farm-assignment')}>
              <div className={cn('survey-item-label')}>
                <FormattedMessage id="component.settings.customSurvey.farmAssignment" />
              </div>
              <ButtonRadioGroup
                name={`farm-assignment-${i}`}
                className={cn('farm-radio')}
                options={farmAssignmentOptions}
                value={all_farms !== false}
                onChange={this.onFarmAssignmentChange(i)}
              />
              {all_farms === false && (
                <AsyncMultiSelect
                  className={cn('farm-select')}
                  values={farms || []}
                  filterOption={farmFilterOption}
                  onSelect={this.onFarmSelect(i)}
                  onRemove={this.onFarmRemove(i)}
                  optionsPath="/farms/search"
                  optionKeys={{ value: 'id', label: 'name' }}
                  requestParams={{ active: true }}
                  placeholderKey="general.choose"
                  extractLabel={(farm) => farm.name}
                />
              )}
            </div>
            <div className={cn('btn-group')}>
              <Button
                className={cn('light', 'preview-btn')}
                onClick={() => this.openPreview(i)}
                disabled={!title}
              >
                <i className="fa fa-remove-red-eye mr-5" />
                <FormattedMessage id="general.preview" />
              </Button>
              {(!this.hasSurveyChanged(i) && id) && (
                <span
                  className={cn('remove-btn')}
                  onClick={() => this.showDeleteConfirmation(i)}
                >
                  <i className="fa fa-trash-o" />
                </span>
              )}
              {(this.hasSurveyChanged(i) || !id) && (
                <>
                  <Button
                    className={cn('primary')}
                    disabled={
                      !(frequency && title) ||
                      (!start_date || start_date.includes('_')) ||
                      this.isEndDateInvalid({ start_date, end_date }) ||
                      this.isIntervalInvalid({ frequency, frequency_interval }) ||
                      this.isSeasonInvalid({ season_start, season_end }) ||
                      this.isReminderInvalid({ reminder_after_no_days }) ||
                      (all_farms === false && !(farms && farms.length))
                    }
                    onClick={() => (id ? this.updateSurvey(i) : this.onSave(i))}
                  >
                    <FormattedMessage id="general.button.save" />
                  </Button>
                  <Button
                    disabled={id ? !this.hasSurveyChanged(i) : i === 0 && this.hasSurveyChanged(i)}
                    onClick={() => (id ? this.cancelEdit(i) : this.removeSurvey(i))}
                  >
                    <FormattedMessage id="general.button.cancel" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        <Button light className={cn('add-survey')} onClick={this.addSurvey}>
          <FormattedMessage id="component.settings.customSurvey.addSurvey" />
        </Button>
      </div>
    );
  }
}

SurveyBox.propTypes = ({
  openModal: T.func.isRequired,
  surveys: T.array.isRequired,
  setSettings: T.func.isRequired,
  countryCode: T.string,
});

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  }),
  { openModal }
)(SurveyBox);
